import { useCallback, useEffect, useRef, useState } from 'react';
import { UseNavigateResult } from '@tanstack/react-router';
import { Authn, navigateToUrl } from '@frontend/auth';
import { AuthStorage, type SsoTenant } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { Button, Heading } from '@frontend/design-system';
import { hydraShellService } from '../services';
import { authFlowTimer, recordAuthFlowSuccess } from '../services/auth-flow-timer';
import { recordAuthResult, restoreAuthFlowOrigin } from '../services/auth-metrics';
import { handlePostAuthRedirect } from '../services/route-utils';
import { rememberSsoTenant, ssoCallbackStore } from '../services/sso-storage';
import { ContentLoader } from './content-loader.tsx';
import { FormWrap } from './form-wrap';
import { SignInError } from './sign-in-error.component';

interface Props<T extends string> {
  auth: Authn;
  navigate: UseNavigateResult<T>;
}

export const SsoSignInCallback = <T extends string>({ auth, navigate }: Props<T>) => {
  const { t } = useTranslation('SignIn');
  const hasStarted = useRef(false);
  const [hasError, setHasError] = useState(false);

  const handleCallback = useCallback(async () => {
    auth.changeAuthMethod('oidc');
    // before the accept flow below clears the login challenge
    restoreAuthFlowOrigin(hydraShellService.getLoginChallenge());
    const ssoTenant = ssoCallbackStore.getItem<SsoTenant>(AuthStorage.sso_tenant);

    // tracks which step failed so IdP code-exchange failures can be told apart from hydra accept failures
    let failureReason = 'handle_callback_failed';
    try {
      // if ssoTenant is undefined, this will throw an error and be caught in the catch block below
      await auth.handleCallback({
        tenant: ssoTenant?.tenant,
        clientId: ssoTenant?.clientId,
        redirectUri: ssoTenant ? new URL(ssoTenant.redirectUri) : undefined,
      });
      failureReason = 'accept_flow_failed';
      const acceptLoginResponse = await hydraShellService.acceptOAuth2Flow();
      if (ssoTenant) {
        rememberSsoTenant(ssoTenant?.tenant);
      }
      ssoCallbackStore.removeItem(AuthStorage.sso_tenant);
      recordAuthResult({ method: 'oidc', outcome: 'success' });
      recordAuthFlowSuccess('oidc');
      handlePostAuthRedirect(acceptLoginResponse.redirectTo || appConfig.DEFAULT_REDIRECT, navigate, {
        reason: 'acceptLoginResponse for oidc SSO',
      });
    } catch (e: any) {
      console.error(e);
      recordAuthResult({ method: 'oidc', outcome: 'fail', failureReason });
      authFlowTimer.recordFailedAttempt();
      // NOTE: keep the original error message out of the captured exception. Backend auth failures
      // surface as e.g. "HTTP error 401", which the global `ignoreErrors` config (see root.tsx)
      // would silently drop -- and this failure mode is exactly the one we need visibility into.
      // sampleRate 1 opts out of the default prod error sampling for the same reason.
      sentry.error({
        error: new Error('SSO sign-in callback failed'),
        topic: 'auth',
        team: 'users-infra',
        sampleRate: 1,
        fingerprint: ['sso-sign-in-callback-failed'],
        addContext: {
          name: 'sso-callback',
          context: {
            originalError: e instanceof Error ? e.stack ?? e.message : String(e),
            tenant: ssoTenant?.tenant,
          },
        },
      });
      setHasError(true);
    }
  }, [auth, navigate]);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;
    handleCallback();
  }, [handleCallback]);

  // Restart the whole flow from the originating app so we get a fresh hydra login challenge and
  // fresh PKCE state. `ssoTeam` routes the user straight back to their IdP (same entry point as
  // an IdP dashboard tile), instead of stranding an SSO-only user on the password form.
  const restartSsoFlow = () => {
    const url = new URL(appConfig.DEFAULT_REDIRECT);
    const tenant = ssoCallbackStore.getItem<SsoTenant>(AuthStorage.sso_tenant)?.tenant;
    if (tenant) {
      url.searchParams.set('ssoTeam', tenant);
    }
    navigateToUrl(url.toString(), { reason: 'retry after SSO sign-in callback error' });
  };

  if (hasError) {
    return (
      <FormWrap>
        <Heading>{t('Log in with SSO')}</Heading>
        <SignInError errorCode='ssoCallbackError' />
        <Button variant='primary' size='large' css={{ width: '100%' }} onClick={restartSsoFlow} id='sso-retry-button'>
          {t('Try again')}
        </Button>
      </FormWrap>
    );
  }

  return (
    <FormWrap>
      <ContentLoader message={t('Logging in...')} show />
    </FormWrap>
  );
};
